/**
 * EditDeductionModal Component
 * Edits a project-level deduction. Same form shape as AddDeductionModal.
 */

import React, { useState, useEffect } from 'react';
import { paymentDeductionAPI } from '../../services/api';
import { toast } from 'react-toastify';

const DEDUCTION_TYPES = [
  { value: 'refund', label: 'Refund' },
  { value: 'late_penalty_interest', label: 'Late Penalty Interest' }
];

const EditDeductionModal = ({ isOpen, onClose, onSuccess, projectId, deduction }) => {
  const [type, setType] = useState('refund');
  const [amount, setAmount] = useState('');
  const [deductionDate, setDeductionDate] = useState('');
  const [reason, setReason] = useState('');
  const [referenceNumber, setReferenceNumber] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (deduction) {
      setType(deduction.type);
      setAmount(deduction.amount?.toString() || '');
      setDeductionDate(
        deduction.deductionDate
          ? new Date(deduction.deductionDate).toISOString().slice(0, 10)
          : ''
      );
      setReason(deduction.reason || '');
      setReferenceNumber(deduction.referenceNumber || '');
    }
  }, [deduction]);

  if (!isOpen || !deduction) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    const parsed = parseFloat(amount);
    if (!parsed || parsed <= 0) {
      toast.error('Enter a valid amount');
      return;
    }
    setSubmitting(true);
    try {
      await paymentDeductionAPI.update(projectId, deduction.id, {
        type,
        amount: parsed,
        deductionDate,
        reason: reason || null,
        referenceNumber: referenceNumber || null
      });
      toast.success('Deduction updated');
      onSuccess();
    } catch (err) {
      console.error('Error updating deduction:', err);
      toast.error(err.response?.data?.error || 'Failed to update deduction');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-gray-500 bg-opacity-75 flex items-end sm:items-center justify-center">
      <div className="absolute inset-0" onClick={onClose} />
      <div className="relative bg-white rounded-t-2xl sm:rounded-lg shadow-xl w-full sm:max-w-lg sm:mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        <form onSubmit={handleSubmit} className="flex flex-col max-h-[90vh]">
          <div className="px-4 sm:px-6 py-4 border-b border-gray-200 flex-shrink-0">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900">Edit Deduction</h3>
          </div>

          <div className="px-4 sm:px-6 py-4 overflow-y-auto space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
              <select
                value={type}
                onChange={e => setType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              >
                {DEDUCTION_TYPES.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Amount (RM)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={amount}
                  onChange={e => setAmount(e.target.value)}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                <input
                  type="date"
                  value={deductionDate}
                  onChange={e => setDeductionDate(e.target.value)}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Reference Number</label>
              <input
                type="text"
                value={referenceNumber}
                onChange={e => setReferenceNumber(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Reason</label>
              <textarea
                rows={3}
                value={reason}
                onChange={e => setReason(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </div>
          </div>

          <div className="px-4 sm:px-6 py-4 border-t border-gray-200 flex flex-col sm:flex-row gap-2 sm:justify-end flex-shrink-0">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm"
            >
              {submitting ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditDeductionModal;
